import { Bell, UserPlus, SlidersHorizontal, Mail, ArrowRight } from 'lucide-react'

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col font-sans text-gray-900">

      {/* ── Nav ── */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 font-bold text-lg text-gray-900">
            <Bell size={20} className="text-sky-600" />
            <span>Radar Concorsi</span>
          </a>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="/auth" className="text-sm font-medium border border-gray-300 rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 hover:bg-gray-50 transition-colors">
              Accedi
            </a>
            <a href="/auth?mode=signup" className="text-sm font-medium bg-sky-600 hover:bg-sky-700 text-white rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 transition-colors">
              Registrati
            </a>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="bg-linear-to-br from-sky-50 via-sky-50 to-white py-20 sm:py-28 px-4 sm:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
            Ricevi{' '}
            <span className="text-sky-600">avvisi sui concorsi pubblici</span>
            {' '}senza doverli cercare
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            Ogni giorno controlliamo inPA per te. Quando esce un bando che corrisponde
            ai tuoi filtri, ti mandiamo una notifica email con il link diretto.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="/auth?mode=signup"
              className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl px-8 py-3.5 text-base transition-colors shadow-sm">
              Inizia gratis
            </a>
            <a href="#come-funziona"
              className="text-sky-600 font-medium text-base hover:underline flex items-center gap-1">
              Scopri come funziona <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="come-funziona" className="bg-white py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Come funziona</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: <UserPlus size={28} className="text-sky-500" />, title: 'Crea un account', text: 'Registrati con la tua email. Nessuna carta di credito.' },
              { icon: <SlidersHorizontal size={28} className="text-sky-500" />, title: 'Imposta un avviso', text: 'Scegli regione, settore e categoria. Puoi aggiungere anche parole chiave per essere più preciso.' },
              { icon: <Mail size={28} className="text-sky-500" />, title: 'Aspetta la email', text: 'Quando esce un bando che corrisponde, ti mandiamo una email con il titolo e il link al bando su inPA.' },
            ].map((step, i) => (
              <div key={i} className="flex flex-col items-center text-center bg-white border border-gray-100 rounded-2xl shadow-sm p-8">
                <div className="mb-4">{step.icon}</div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature highlights ── */}
      <section className="bg-sky-50 py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Aggiornamenti quotidiani sui nuovi bandi in uscita</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { title: 'Monitoraggio giornaliero', text: 'Ogni giorno scarichiamo i bandi appena pubblicati su inPA e li confrontiamo con i tuoi filtri.' },
              { title: 'Solo quello che cerchi', text: 'Filtra per regione, settore, categoria e parole chiave. Nessuna email inutile.' },
              { title: 'Link diretto al bando', text: 'Nella notifica trovi il titolo del concorso e il link alla pagina su inPA. Nient\'altro.' },
            ].map((f, i) => (
              <div key={i} className="bg-white rounded-2xl border border-sky-100 p-8 shadow-sm">
                <div className="w-8 h-1 bg-sky-400 rounded mb-4" />
                <h3 className="text-base font-semibold mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-white py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Domande frequenti</h2>
          <div className="divide-y divide-gray-100">
            {[
              {
                q: 'Come faccio ad essere avvisato sui nuovi concorsi pubblici?',
                a: 'Registrati, crea un avviso con i filtri che vuoi (regione, settore, parole chiave) e aspetta la email. Quando esce un bando che corrisponde, ti scriviamo noi.',
              },
              {
                q: 'Da dove arrivano i bandi?',
                a: 'Da inPA, il portale ufficiale del Governo italiano per i concorsi pubblici. Copre la quasi totalità dei bandi della Pubblica Amministrazione italiana.',
              },
              {
                q: 'Con che frequenza ricevo aggiornamenti sui concorsi?',
                a: 'Una volta al giorno, se ci sono bandi nuovi che corrispondono ai tuoi filtri. Se non esce nulla di nuovo, non ricevi niente.',
              },
              {
                q: 'Posso filtrare i concorsi per regione o settore?',
                a: 'Sì. Puoi filtrare per regione, settore, categoria e parole chiave. Puoi anche creare più avvisi con criteri diversi.',
              },
              {
                q: 'Cosa contiene la notifica email?',
                a: 'Il titolo del concorso, l\'ente che lo pubblica e il link diretto alla pagina su inPA.',
              },
            ].map((item, i) => (
              <div key={i} className="py-6">
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="bg-linear-to-br from-sky-600 to-sky-600 py-20 px-4 sm:px-6 text-center text-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Smetti di controllare inPA ogni giorno</h2>
          <p className="text-sky-200 text-lg mb-8">
            Imposta un avviso e ci pensiamo noi. Quando esce qualcosa di nuovo, ti scriviamo.
          </p>
          <a href="/auth?mode=signup"
            className="inline-block bg-white text-sky-700 hover:bg-sky-50 font-semibold rounded-xl px-8 py-3.5 text-base transition-colors shadow-sm">
            Registrati gratis
          </a>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-gray-100 py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <span>© 2026 Radar Concorsi — Il servizio di notifiche per i concorsi pubblici</span>
          <div className="flex gap-6">
            <a href="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</a>
            <a href="/termini" className="hover:text-gray-600 transition-colors">Termini di servizio</a>
          </div>
        </div>
      </footer>

    </div>
  )
}
